/**
 * The safe triangle: the whole submenu-intent mechanism, as one pure function.
 *
 * A menu that closes an open submenu the moment the pointer leaves the row that
 * opened it is unusable, because the natural path to the submenu is diagonal
 * and crosses the rows in between. The fix is to ask a geometric question
 * instead of a "which row am I over" question: is the pointer still inside the
 * wedge that spans from where it started to the two near corners of the open
 * submenu? While it is, the pointer is heading for the submenu and the
 * scheduled close is held off.
 *
 * This module reads no DOM, holds no state and starts no timers. The caller
 * measures the submenu, remembers where the pointer was and owns the timers,
 * so the only part that is easy to get wrong is testable without a browser.
 */

import type { Rect } from './types'

/** A point in viewport coordinates, which is what the caller samples. */
export interface Point {
  x: number
  y: number
}

/** A rect with its edges in ascending order, whatever order they arrived in. */
interface Bounds {
  left: number
  right: number
  top: number
  bottom: number
}

function boundsOf(rect: Rect): Bounds {
  return {
    left: Math.min(rect.x, rect.x + rect.width),
    right: Math.max(rect.x, rect.x + rect.width),
    top: Math.min(rect.y, rect.y + rect.height),
    bottom: Math.max(rect.y, rect.y + rect.height),
  }
}

/**
 * Twice the signed area of the triangle `p`, `a`, `b`.
 *
 * The sign says which side of the directed line `a` to `b` the point `p` falls
 * on, and zero says it is exactly on that line.
 */
function side(p: Point, a: Point, b: Point): number {
  return (a.x - p.x) * (b.y - p.y) - (a.y - p.y) * (b.x - p.x)
}

/**
 * The x of the target's near vertical edge.
 *
 * A submenu is always to one side of the pointer, so the two near corners are
 * the two ends of the edge facing it. An origin that is horizontally inside the
 * target is not a case the menu produces, but it costs one comparison to answer
 * it with the closer edge rather than with a coin toss.
 */
function nearEdgeX(origin: Point, bounds: Bounds): number {
  if (origin.x <= bounds.left) return bounds.left
  if (origin.x >= bounds.right) return bounds.right

  return origin.x - bounds.left <= bounds.right - origin.x ? bounds.left : bounds.right
}

function withinBoundingBox(p: Point, a: Point, b: Point, c: Point): boolean {
  return (
    p.x >= Math.min(a.x, b.x, c.x) &&
    p.x <= Math.max(a.x, b.x, c.x) &&
    p.y >= Math.min(a.y, b.y, c.y) &&
    p.y <= Math.max(a.y, b.y, c.y)
  )
}

/**
 * Is `p` inside the triangle formed by `origin` and the two near corners of
 * `target`, or inside `target` itself?
 *
 * The rect counts as inside because a pointer that has arrived at the submenu
 * has plainly not lost interest in it, and because the triangle stops at the
 * near edge.
 *
 * Edges are inclusive: the standard same-side test treats a zero cross product
 * as "not on the wrong side", so a point exactly on an edge, on a corner, or on
 * the origin is inside. The bounding-box check that follows it exists for the
 * degenerate cases: when the three vertices are collinear every cross product
 * is zero and the same-side test alone would accept the whole infinite line. A
 * proper triangle is contained by its own bounding box, so the extra check
 * never rejects a point the same-side test should have accepted.
 *
 * Nothing here throws: a zero-area or negatively sized target is normalised and
 * answered, not rejected.
 */
export function inTriangle(p: Point, origin: Point, target: Rect): boolean {
  const bounds = boundsOf(target)

  if (p.x >= bounds.left && p.x <= bounds.right && p.y >= bounds.top && p.y <= bounds.bottom) {
    return true
  }

  const edgeX = nearEdgeX(origin, bounds)
  const near1: Point = { x: edgeX, y: bounds.top }
  const near2: Point = { x: edgeX, y: bounds.bottom }

  const d1 = side(p, origin, near1)
  const d2 = side(p, near1, near2)
  const d3 = side(p, near2, origin)

  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0

  if (hasNegative && hasPositive) return false

  return withinBoundingBox(p, origin, near1, near2)
}
